import {computed, Injectable, signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {DateTime, DateTimeMaybeValid, DateTimeUnit, Duration} from 'luxon';
import {interval, map, startWith} from 'rxjs';
import {BaseComponent} from '../../base-component';
import {TimeUtils} from '../../common/utils/time-utils';
import {DayCellData} from '../planning-display/planning-grid/day-cell-data';
import {TimeViewMode} from '../time-view-mode.enum';

@Injectable()
export class TimelineService extends BaseComponent {
  public readonly ROW_INDICATOR_WIDTH = 184;
  public readonly COLUMN_HEADER_HEIGHT = 96;
  public readonly MILLISECONDS_IN_MINUTE = 60000;

  private readonly MAX_CELLS_PER_DAY = 8;
  private readonly DEFAULT_CELLS_PER_DAY = 4;
  private readonly MAX_CELLS_PER_DAY_FOR_HOUR_VIEW = 12;

  public currentTime = toSignal(
    interval(TimeUtils.MINUTE_IN_MS).pipe(
      startWith(0),
      map(() => new Date())
    ),
    {initialValue: new Date()}
  );

  public rowWidth = signal<number>(0);
  public timeView = signal(TimeViewMode.DAY);
  private _targetStart = signal<DateTimeMaybeValid>(DateTime.now());
  private _targetEnd = signal<DateTimeMaybeValid>(DateTime.now());
  private _timeUnit = computed<DateTimeUnit>(() => (this.timeView() === TimeViewMode.DAY ? 'day' : 'hour'));
  private _cellsPerHour = signal<number>(this.MAX_CELLS_PER_DAY_FOR_HOUR_VIEW);
  private _cellsPerDay = signal<number>(this.DEFAULT_CELLS_PER_DAY);

  public displayedStart = computed(() => this._targetStart().startOf(this._timeUnit()));
  public displayedEnd = computed(() =>
    this._targetEnd()
      .startOf(this._timeUnit())
      .plus(this._timeUnit() === 'day' ? {days: 1} : {hours: 0})
  );

  public displayedDuration = computed(() => this.displayedEnd().diff(this.displayedStart()));

  public dayCellData = computed<readonly DayCellData[]>(() => {
    const cells: DayCellData[] = [];
    const timeInterval = this.timeView() === TimeViewMode.DAY ? 'hours' : 'minutes';
    const currentCells = this.timeView() === TimeViewMode.DAY ? this._cellsPerDay() : this._cellsPerHour();
    const normalCellDuration = this.timeView() === TimeViewMode.DAY ? 24 / this._cellsPerDay() : 60 / this._cellsPerHour();
    const end = this.displayedEnd();
    let currentDate = this.displayedStart();
    while (currentDate < end) {
      const dayCell: DayCellData = {date: currentDate.toJSDate(), duration: currentDate.plus({days: 1}).diff(currentDate).as('hours'), items: []};
      cells.push(dayCell);

      for (let cellIndex = 1; cellIndex <= currentCells; cellIndex++) {
        const dateForCell = currentDate.toJSDate();

        currentDate = currentDate.plus(timeInterval === 'hours' ? {hour: normalCellDuration} : {minute: normalCellDuration});
        dayCell.items.push({date: dateForCell, duration: currentDate.diff(DateTime.fromJSDate(dateForCell)).as('minutes')});
      }
    }
    return cells;
  });

  public constructor() {
    super();
  }

  public setStartDate(value: DateTime): void {
    this._targetStart.set(value);
  }

  public setEndDate(value: DateTime): void {
    this._targetEnd.set(value);
  }

  public setCellsPerDay(newCellsPerDay: number): void {
    this._cellsPerDay.set(Math.min(Math.max(newCellsPerDay, 1), this.MAX_CELLS_PER_DAY));
  }

  public setCellsPerHour(newCellsPerHour: number): void {
    this._cellsPerHour.set(Math.min(Math.max(newCellsPerHour, 1), this.MAX_CELLS_PER_DAY_FOR_HOUR_VIEW));
  }

  public getLeftPercentForDate(start: DateTime): number {
    return (start.diff(this.displayedStart()).as('minutes') / this.displayedDuration().as('minutes')) * 100;
  }

  public getLeftPositionForDate(start: DateTime): number {
    return Math.round((start.diff(this.displayedStart()).as('minutes') / this.displayedDuration().as('minutes')) * this.rowWidth());
  }

  public getWidthForDuration(duration: Duration): number {
    return Math.round((duration.as('minutes') / this.displayedDuration().as('minutes')) * this.rowWidth());
  }

  public getDateForHorizontalPosition(positionX: number): Date {
    const minutesForPosition = (positionX / this.rowWidth()) * this.displayedDuration().as('minutes');
    return this.displayedStart().plus({minutes: minutesForPosition}).toJSDate();
  }

  public getDateForHorizontalPercentage(percentage: number): Date {
    const minutesForPosition = percentage * this.displayedDuration().as('minutes');
    return this._targetStart().plus({minutes: minutesForPosition}).toJSDate();
  }

  public setTimeToHour(hour: Date): void {
    this._targetStart.update(() => DateTime.fromJSDate(new Date(hour)));
    this._targetEnd.update(() => DateTime.fromJSDate(hour).plus({hour: 1}));
  }
}
